import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { faqService, contentService } from '@/services/api';
import { 
  HelpCircle, 
  MessageSquare, 
  Phone, 
  Mail, 
  Search,
  FileText,
  Shield,
  ChevronRight,
  Send,
  Loader2,
  Clock,
  MapPin
} from 'lucide-react';

const HelpPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const [searchQuery, setSearchQuery] = useState('');
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);

  // Default FAQs as fallback
  const defaultFaqs = [
    {
      question: lang === 'ar' ? 'كيف أحجز موعداً مع طبيب؟' : 'How do I book an appointment?',
      answer: lang === 'ar' 
        ? 'اختر الطبيب من قائمة الأطباء، ثم اختر التاريخ والوقت المناسب، وأكمل عملية الحجز.'
        : 'Select a doctor from the doctors list, choose a suitable date and time, and complete the booking process.',
    },
    {
      question: lang === 'ar' ? 'كيف يمكنني إلغاء موعدي؟' : 'How can I cancel my appointment?',
      answer: lang === 'ar'
        ? 'اذهب إلى صفحة المواعيد، اختر الموعد الذي تريد إلغاءه، ثم اضغط على زر الإلغاء.'
        : 'Go to the Appointments page, select the appointment you want to cancel, and click the cancel button.',
    },
    {
      question: lang === 'ar' ? 'ما هي طرق الدفع المتاحة؟' : 'What payment methods are available?',
      answer: lang === 'ar'
        ? 'نقبل الدفع النقدي في العيادة، البطاقات الائتمانية، والتأمين الصحي.'
        : 'We accept cash payment at the clinic, credit cards, and health insurance.',
    },
    {
      question: lang === 'ar' ? 'كيف أضيف طبيباً إلى المفضلة؟' : 'How do I add a doctor to favorites?',
      answer: lang === 'ar'
        ? 'اضغط على أيقونة القلب في بطاقة الطبيب لإضافته إلى قائمة المفضلة.'
        : 'Click the heart icon on the doctor card to add them to your favorites list.',
    },
    {
      question: lang === 'ar' ? 'هل يمكنني تغيير موعدي؟' : 'Can I reschedule my appointment?',
      answer: lang === 'ar'
        ? 'نعم، اذهب إلى صفحة المواعيد واضغط على "إعادة جدولة" لاختيار موعد جديد.'
        : 'Yes, go to the Appointments page and click "Reschedule" to select a new time.',
    },
    {
      question: lang === 'ar' ? 'كيف أتواصل مع طبيبي؟' : 'How do I contact my doctor?',
      answer: lang === 'ar'
        ? 'استخدم ميزة الرسائل للتواصل مع طبيبك مباشرة من خلال التطبيق.'
        : 'Use the Messages feature to communicate with your doctor directly through the app.',
    },
  ];

  // Fetch FAQs and settings from API
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch FAQs
        const faqResponse = await faqService.getAll();
        if (faqResponse.data?.data && Array.isArray(faqResponse.data.data)) {
          const transformedFaqs = faqResponse.data.data.map(faq => ({
            question: lang === 'ar' ? (faq.question_ar || faq.question) : (faq.question_en || faq.question),
            answer: lang === 'ar' ? (faq.answer_ar || faq.answer) : (faq.answer_en || faq.answer),
          }));
          setFaqs(transformedFaqs.length > 0 ? transformedFaqs : defaultFaqs);
        } else {
          setFaqs(defaultFaqs);
        }

        // Fetch settings for contact info
        try {
          const settingsResponse = await contentService.getSettings();
          if (settingsResponse.data?.data) {
            setSettings(settingsResponse.data.data);
          }
        } catch (e) {
          console.log('Settings not available');
        }
      } catch (error) {
        console.error('Failed to fetch FAQs:', error);
        setFaqs(defaultFaqs);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [lang]);

  const filteredFaqs = faqs.filter(faq => 
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success(
      lang === 'ar' ? 'تم إرسال رسالتك بنجاح' : 'Your message has been sent',
      { description: lang === 'ar' ? 'سنرد عليك قريباً' : 'We will get back to you soon' }
    );
    setContactForm({ name: '', email: '', message: '' });
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="help-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <div className="container mx-auto px-4 py-12">
          <div className="flex items-center gap-3 mb-4">
            <HelpCircle className="h-8 w-8" />
            <h1 className="text-3xl font-bold">
              {lang === 'ar' ? 'المساعدة والدعم' : 'Help & Support'}
            </h1>
          </div>
          <p className="text-teal-100 max-w-xl">
            {lang === 'ar' 
              ? 'ابحث في الأسئلة الشائعة أو تواصل معنا للحصول على المساعدة'
              : 'Search our FAQs or contact us for assistance'
            }
          </p>
          
          {/* Search */}
          <div className="mt-6 max-w-xl">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                placeholder={lang === 'ar' ? 'ابحث عن سؤالك...' : 'Search for your question...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/60 focus:bg-white focus:text-slate-900 focus:placeholder:text-slate-400"
                data-testid="search-faq"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <Tabs defaultValue="faq" className="space-y-6">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2">
            <TabsTrigger value="faq">
              <FileText className="h-4 w-4 mr-2" />
              {lang === 'ar' ? 'الأسئلة الشائعة' : 'FAQs'}
            </TabsTrigger>
            <TabsTrigger value="contact">
              <MessageSquare className="h-4 w-4 mr-2" />
              {lang === 'ar' ? 'تواصل معنا' : 'Contact Us'}
            </TabsTrigger>
          </TabsList>

          {/* FAQs */}
          <TabsContent value="faq">
            <Card className="dark:bg-slate-800 border-0 shadow-sm max-w-3xl mx-auto">
              <CardContent className="pt-6">
                {loading ? (
                  <div className="space-y-4">
                    {[1, 2, 3, 4].map(i => (
                      <div key={i} className="space-y-2">
                        <Skeleton className="h-6 w-3/4" />
                        <Skeleton className="h-4 w-full" />
                      </div>
                    ))}
                  </div>
                ) : filteredFaqs.length === 0 ? (
                  <div className="text-center py-8">
                    <HelpCircle className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 dark:text-slate-400">
                      {lang === 'ar' ? 'لم يتم العثور على نتائج' : 'No results found'}
                    </p>
                  </div>
                ) : (
                  <Accordion type="single" collapsible className="w-full">
                    {filteredFaqs.map((faq, index) => (
                      <AccordionItem key={index} value={`item-${index}`}>
                        <AccordionTrigger className="text-left hover:no-underline">
                          {faq.question}
                        </AccordionTrigger>
                        <AccordionContent className="text-slate-600 dark:text-slate-400">
                          {faq.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contact */}
          <TabsContent value="contact">
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {/* Contact Form */}
              <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                <CardHeader>
                  <CardTitle className="text-lg">
                    {lang === 'ar' ? 'أرسل لنا رسالة' : 'Send us a message'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label>{lang === 'ar' ? 'الاسم' : 'Name'}</Label>
                      <Input
                        value={contactForm.name}
                        onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                        required
                        data-testid="contact-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{lang === 'ar' ? 'البريد الإلكتروني' : 'Email'}</Label>
                      <Input
                        type="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                        required
                        data-testid="contact-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{lang === 'ar' ? 'الرسالة' : 'Message'}</Label>
                      <Textarea
                        value={contactForm.message}
                        onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                        rows={4}
                        required
                        data-testid="contact-message"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-teal-600 hover:bg-teal-700"
                      disabled={submitting}
                      data-testid="submit-contact"
                    >
                      {submitting ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{lang === 'ar' ? 'جارٍ الإرسال...' : 'Sending...'}</>
                      ) : (
                        <><Send className="h-4 w-4 mr-2" />{lang === 'ar' ? 'إرسال' : 'Send'}</>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Contact Info */}
              <div className="space-y-4">
                <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-full bg-teal-100">
                        <Phone className="h-6 w-6 text-teal-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">
                          {lang === 'ar' ? 'اتصل بنا' : 'Call Us'}
                        </p>
                        <p className="text-teal-600">+963 11 123 4567</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-full bg-teal-100">
                        <Mail className="h-6 w-6 text-teal-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">
                          {lang === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                        </p>
                        <p className="text-teal-600">support@dactrha.com</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-full bg-teal-100">
                        <Clock className="h-6 w-6 text-teal-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">
                          {lang === 'ar' ? 'ساعات العمل' : 'Working Hours'}
                        </p>
                        <p className="text-slate-600 dark:text-slate-400">
                          {lang === 'ar' ? 'السبت - الخميس: 9 ص - 9 م' : 'Sat - Thu: 9AM - 9PM'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-full bg-teal-100">
                        <MapPin className="h-6 w-6 text-teal-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">
                          {lang === 'ar' ? 'العنوان' : 'Address'}
                        </p>
                        <p className="text-slate-600 dark:text-slate-400">
                          {lang === 'ar' ? 'دمشق، سوريا' : 'Damascus, Syria'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default HelpPage;
